import { computed } from 'vue'
import type { ConflictInfo, ScheduleOccurrenceDraft, ScheduleSlot } from '@/types'
import { useScheduleStore } from '@/stores/schedule'
import type {
  CombinedClassRule,
  ForbiddenRequiredItem,
  ScheduleRuleSettings,
  TimeSlot
} from '@/views/next/schedule/wizard/steps/rules/types'

/**
 * 对调/联动方案：把一次落格涉及的所有课时按去向分成三方。
 *
 * - moverGroup：目标侧（→ 目标槽位），含被拖课时及其联动组
 * - returningGroup：源侧（→ 被拖课时的源槽位，各自班级），目标格占位课及其联动闭包
 * - displacedGroup：目标侧（→ 目标槽位），回源成员在自己班级源格上的占位课（被顶替）
 * - reject：硬拒绝原因（force 也不可绕过）
 */
export interface LinkedSwapPlan {
  moverGroup: ScheduleOccurrenceDraft[]
  returningGroup: ScheduleOccurrenceDraft[]
  displacedGroup: ScheduleOccurrenceDraft[]
  reject: {
    kind: 'both-linked' | 'displaced-linked'
    occupant: ScheduleOccurrenceDraft
    partner: ScheduleOccurrenceDraft
  } | null
}

type UseScheduleConflictsReturn = {
  draftToSlot: (occurrence: ScheduleOccurrenceDraft) => ScheduleSlot
  buildConflictInfo: (
    occurrences: ScheduleOccurrenceDraft[],
    movingOccurrence: ScheduleOccurrenceDraft,
    dayOfWeek: number,
    period: number,
    excludeKeys?: Set<string>
  ) => ConflictInfo
  resolveLinkedOccurrences: (movingKey: string) => ScheduleOccurrenceDraft[]
  isCombinedClassPair: (
    left: Pick<ScheduleOccurrenceDraft, 'courseId' | 'classId' | 'subgroupId'>,
    right: Pick<ScheduleOccurrenceDraft, 'courseId' | 'classId' | 'subgroupId'>,
    ruleSettings: ScheduleRuleSettings | undefined
  ) => boolean
  buildLinkedSwapPlan: (
    movingKey: string,
    dayOfWeek: number,
    period: number
  ) => LinkedSwapPlan | null
  linkedSwapRejectMessage: (
    reject: NonNullable<LinkedSwapPlan['reject']>
  ) => string
  checkDragConflict: (
    movingKey: string,
    dayOfWeek: number,
    period: number
  ) => { status: 'green' | 'yellow' | 'red'; message: string; hard?: boolean }
}

/**
 * 冲突检测的数据来源。
 * 缺省时读取全局排课 store；独立页面（如新版排课详情）可注入自己的草稿与规则，
 * 复用同一套冲突/互换算法而不依赖 Pinia。
 */
export interface ScheduleConflictSource {
  getOccurrences: () => ScheduleOccurrenceDraft[]
  getRuleSettings: () => ScheduleRuleSettings | undefined
}

/**
 * 冲突检测逻辑 composable
 *
 * 职责：
 * - 检测排课冲突（班级、教师、教室）
 * - 构建冲突信息
 */
export function useScheduleConflicts(source?: ScheduleConflictSource): UseScheduleConflictsReturn {
  /**
   * 拖拽预演只关心指定课位中的候选。索引随草稿对象的放置字段自动失效，
   * 一次网格预演中复用，避免每个目标格反复扫描全任务所有班级。
   * computed 内延迟取得 store，避免 schedule store 初始化时形成循环依赖。
   */
  const occurrenceIndex = computed(() => {
    const occurrences = source ? source.getOccurrences() : useScheduleStore().draftOccurrences
    const byKey = new Map<string, ScheduleOccurrenceDraft>()
    const placedBySlot = new Map<string, ScheduleOccurrenceDraft[]>()
    for (const occurrence of occurrences) {
      byKey.set(occurrence.key, occurrence)
      if (!occurrence.placed || occurrence.dayOfWeek == null || occurrence.period == null) {
        continue
      }
      const slotKey = `${occurrence.dayOfWeek}:${occurrence.period}`
      const placed = placedBySlot.get(slotKey) ?? []
      placed.push(occurrence)
      placedBySlot.set(slotKey, placed)
    }
    return { occurrences, byKey, placedBySlot }
  })

  function occurrencesAt(
    day: number | null | undefined,
    period: number | null | undefined
  ): ScheduleOccurrenceDraft[] {
    if (day == null || period == null) return []
    return occurrenceIndex.value.placedBySlot.get(`${day}:${period}`) ?? []
  }

  /** 课时的全部任教教师 ID（多师同堂）；无列表时回退到单教师字段 */
  function occurrenceTeacherIds(occurrence: ScheduleOccurrenceDraft): number[] {
    if (Array.isArray(occurrence.teacherIds) && occurrence.teacherIds.length > 0) {
      return occurrence.teacherIds
    }
    return occurrence.teacherId != null ? [occurrence.teacherId] : []
  }

  /** 两个课时的任教教师集合是否有交集 */
  function sharesTeacher(a: ScheduleOccurrenceDraft, b: ScheduleOccurrenceDraft): boolean {
    const teacherIdsB = occurrenceTeacherIds(b)
    return occurrenceTeacherIds(a).some((teacherId) => teacherIdsB.includes(teacherId))
  }

  /** 按教师 ID 取课时的教师显示名（多师同堂时定位到具体那位） */
  function teacherNameOf(occurrence: ScheduleOccurrenceDraft, teacherId: number): string {
    const ids = occurrenceTeacherIds(occurrence)
    const names = Array.isArray(occurrence.teacherNames) ? occurrence.teacherNames : []
    const index = ids.indexOf(teacherId)
    if (index >= 0 && names[index]) {
      return names[index]
    }
    return occurrence.teacherName
  }

  /**
   * 将草稿课时转换为 ScheduleSlot 格式
   */
  function draftToSlot(occurrence: ScheduleOccurrenceDraft): ScheduleSlot {
    const teacherIds = occurrenceTeacherIds(occurrence)
    const teacherNames = Array.isArray(occurrence.teacherNames) && occurrence.teacherNames.length > 0
      ? occurrence.teacherNames
      : occurrence.teacherName ? [occurrence.teacherName] : []
    const teacherName = teacherNames.length > 0 ? teacherNames.join('、') : occurrence.teacherName
    return {
      day: occurrence.dayOfWeek ?? 0,
      period: occurrence.period ?? 0,
      classId: occurrence.classId ?? null,
      className: occurrence.className,
      subgroupId: occurrence.subgroupId ?? null,
      subgroupName: occurrence.subgroupName ?? null,
      courseId: occurrence.courseId ?? null,
      courseName: occurrence.courseName,
      course: occurrence.courseName,
      teacherId: occurrence.teacherId ?? (teacherIds.length > 0 ? teacherIds[0] : null),
      teacherName,
      teacher: teacherName,
      teacherIds,
      teacherNames,
      roomId: occurrence.roomId ?? null,
      roomName: occurrence.roomName ?? null,
      duration: occurrence.duration,
      taskActivityId: occurrence.taskActivityId,
      occurrenceIndex: occurrence.occurrenceIndex,
      occurrenceKey: occurrence.key,
      placed: occurrence.placed
    }
  }

  /**
   * 构建冲突信息
   *
   * 检测移动某个课时到指定时段时是否会产生冲突：
   * - 班级冲突：同一班级在同一时段已有其他课程
   * - 教师冲突：同一教师在同一时段已有其他课程
   * - 教室冲突：同一教室在同一时段已被占用
   *
   * @param occurrences 所有草稿课时
   * @param movingOccurrence 正在移动的课时
   * @param dayOfWeek 目标星期几
   * @param period 目标节次
   * @returns 冲突信息对象
   */
  function buildConflictInfo(
    occurrences: ScheduleOccurrenceDraft[],
    movingOccurrence: ScheduleOccurrenceDraft,
    dayOfWeek: number,
    period: number,
    excludeKeys?: Set<string>
  ): ConflictInfo {
    for (const occurrence of occurrences) {
      // 跳过自身
      if (occurrence.key === movingOccurrence.key) {
        continue
      }
      // 跳过联动组内成员（组内豁免，由外层整组统一判定）
      if (excludeKeys?.has(occurrence.key)) {
        continue
      }
      // 跳过未放置的课时
      if (!occurrence.placed || occurrence.dayOfWeek == null || occurrence.period == null) {
        continue
      }
      // 跳过不同时段的课时
      if (occurrence.dayOfWeek !== dayOfWeek || occurrence.period !== period) {
        continue
      }

      const conflictingSlot = draftToSlot(occurrence)

      // 检测班级冲突
      if (hasClassConflict(movingOccurrence, occurrence)) {
        return {
          hasConflict: true,
          conflictType: 'class',
          message: '该班级该时段已存在排课。',
          conflictingSlot
        }
      }

      // 检测教师冲突（多师同堂：任一共同任教教师被占用即冲突；合班上课：同课程多班/分组共享教师，不算冲突）
      if (
        sharesTeacher(movingOccurrence, occurrence)
        && !isCombinedClassPair(movingOccurrence, occurrence, getRuleSettings())
      ) {
        return {
          hasConflict: true,
          conflictType: 'teacher',
          message: '该教师该时段已存在排课。',
          conflictingSlot
        }
      }

      // 检测教室冲突
      if (
        movingOccurrence.roomId != null &&
        occurrence.roomId != null &&
        occurrence.roomId === movingOccurrence.roomId
      ) {
        return {
          hasConflict: true,
          conflictType: 'room',
          message: '该教室该时段已被占用。',
          conflictingSlot
        }
      }
    }

    // 无冲突
    return {
      hasConflict: false,
      conflictType: 'none',
      message: ''
    }
  }

  function hasClassConflict(
    movingOccurrence: ScheduleOccurrenceDraft,
    occurrence: ScheduleOccurrenceDraft
  ): boolean {
    if (movingOccurrence.classId == null || occurrence.classId !== movingOccurrence.classId) {
      return false
    }
    const movingSubgroupId = movingOccurrence.subgroupId ?? null
    const existingSubgroupId = occurrence.subgroupId ?? null
    return movingSubgroupId === null || existingSubgroupId === null || movingSubgroupId === existingSubgroupId
  }

  function getRuleSettings(): ScheduleRuleSettings | undefined {
    return source
      ? source.getRuleSettings()
      : useScheduleStore().currentTask?.rulesConfig?.ruleSettings
  }

  function matchesCombinedTarget(
    rule: CombinedClassRule,
    classId: number,
    subgroupId: number | null
  ): boolean {
    if (rule.targets && rule.targets.length > 0) {
      return rule.targets.some(
        (target) =>
          target.classId === classId
          && (target.subgroupId ?? null) === subgroupId
      )
    }
    if (rule.subgroupIds && rule.subgroupIds.length > 0) {
      return subgroupId != null && rule.subgroupIds.includes(subgroupId)
    }
    // 旧版仅按班级合班：不区分分组
    return rule.classIds.includes(classId)
  }

  /**
   * 校验两节课是否属于同一合班规则（同课程，且双方目标均在规则内）。
   */
  function isCombinedClassPair(
    left: Pick<ScheduleOccurrenceDraft, 'courseId' | 'classId' | 'subgroupId'>,
    right: Pick<ScheduleOccurrenceDraft, 'courseId' | 'classId' | 'subgroupId'>,
    ruleSettings: ScheduleRuleSettings | undefined
  ): boolean {
    if (left.courseId == null || right.courseId == null) return false
    if (left.courseId !== right.courseId) return false
    if (left.classId == null || right.classId == null) return false
    if (!ruleSettings?.combinedClassRules?.length) return false

    const leftSubgroupId = left.subgroupId ?? null
    const rightSubgroupId = right.subgroupId ?? null
    // 捕获为局部常量：闭包内无法沿用上方判空收窄，避免非空断言
    const leftClassId = left.classId
    const rightClassId = right.classId

    return ruleSettings.combinedClassRules.some((rule: CombinedClassRule) => {
      if (rule.courseId !== left.courseId) return false
      return (
        matchesCombinedTarget(rule, leftClassId, leftSubgroupId)
        && matchesCombinedTarget(rule, rightClassId, rightSubgroupId)
      )
    })
  }

  /**
   * 是否处于同一课位（均已放置且星期/节次相同）。
   */
  function isSameSlot(
    left: Pick<ScheduleOccurrenceDraft, 'placed' | 'dayOfWeek' | 'period'>,
    right: Pick<ScheduleOccurrenceDraft, 'placed' | 'dayOfWeek' | 'period'>
  ): boolean {
    if (!left.placed || !right.placed) return false
    if (left.dayOfWeek == null || left.period == null) return false
    return left.dayOfWeek === right.dayOfWeek && left.period === right.period
  }

  /**
   * 解析某课时的联动组（含自身）。
   *
   * 联动来源（按 rulesConfig 中的关系规则）：
   * - 合班上课：同课程、同 occurrenceIndex、当前同槽位的同规则成员
   * - 同时开课：同规则其他 pair（classId+courseId）、当前同槽位的课时
   * - 单双周：同班级配对课程（oddCourseId/evenCourseId 互换）、当前同槽位的课时
   *
   * 取闭包：组成员命中的新伙伴继续向外扩展，直到收敛
   * （可处理"合班+单双周"等叠加场景）。
   * 未放置（暂存区）的课时没有同槽位伙伴，组即其自身。
   */
  function resolveLinkedOccurrences(movingKey: string): ScheduleOccurrenceDraft[] {
    const moving = occurrenceIndex.value.byKey.get(movingKey)
    if (!moving) {
      return []
    }
    const ruleSettings = getRuleSettings()
    if (!ruleSettings) {
      return [moving]
    }

    const group = new Map<string, ScheduleOccurrenceDraft>([[moving.key, moving]])

    const collectPartners = (member: ScheduleOccurrenceDraft): ScheduleOccurrenceDraft[] => {
      const partners: ScheduleOccurrenceDraft[] = []
      if (member.classId == null || member.courseId == null) {
        return partners
      }
      const sameSlotOccurrences = occurrencesAt(member.dayOfWeek, member.period)

      // 合班上课：同课程 + 同 occurrenceIndex + 当前同槽位 + 双方均命中同一条合班规则
      for (const rule of ruleSettings.combinedClassRules ?? []) {
        if (rule.courseId !== member.courseId) continue
        if (!matchesCombinedTarget(rule, member.classId, member.subgroupId ?? null)) continue
        for (const candidate of sameSlotOccurrences) {
          if (candidate.key === member.key || group.has(candidate.key)) continue
          if (candidate.courseId !== member.courseId) continue
          if (candidate.occurrenceIndex !== member.occurrenceIndex) continue
          if (candidate.classId == null) continue
          if (!isSameSlot(member, candidate)) continue
          if (!matchesCombinedTarget(rule, candidate.classId, candidate.subgroupId ?? null)) continue
          partners.push(candidate)
        }
      }

      // 同时开课：命中某 pair 后，同规则其他 pair 的当前同槽位课时
      for (const rule of ruleSettings.simultaneousRules ?? []) {
        const hit = rule.pairs.some(
          (pair) => pair.classId === member.classId && pair.courseId === member.courseId
        )
        if (!hit) continue
        for (const pair of rule.pairs) {
          if (pair.classId === member.classId && pair.courseId === member.courseId) continue
          for (const candidate of sameSlotOccurrences) {
            if (candidate.key === member.key || group.has(candidate.key)) continue
            if (candidate.classId !== pair.classId || candidate.courseId !== pair.courseId) continue
            if (!isSameSlot(member, candidate)) continue
            partners.push(candidate)
          }
        }
      }

      // 单双周：同班级配对课程的当前同槽位课时
      for (const rule of ruleSettings.oddEvenWeekRules ?? []) {
        if (rule.classId !== member.classId) continue
        let pairedCourseId: number | null = null
        if (member.courseId === rule.oddCourseId) {
          pairedCourseId = rule.evenCourseId
        } else if (member.courseId === rule.evenCourseId) {
          pairedCourseId = rule.oddCourseId
        }
        if (pairedCourseId == null) continue
        for (const candidate of sameSlotOccurrences) {
          if (candidate.key === member.key || group.has(candidate.key)) continue
          if (candidate.classId !== member.classId || candidate.courseId !== pairedCourseId) continue
          if (!isSameSlot(member, candidate)) continue
          partners.push(candidate)
        }
      }

      return partners
    }

    // 闭包扩展：新成员的伙伴继续纳入，直到收敛
    const queue = [moving]
    while (queue.length > 0) {
      const member = queue.shift()
      if (!member) break
      for (const partner of collectPartners(member)) {
        if (!group.has(partner.key)) {
          group.set(partner.key, partner)
          queue.push(partner)
        }
      }
    }

    return [...group.values()]
  }

  /**
   * 构建一次落格的对调方案（预检与落定共用，保证两者判定一致）。
   *
   * 规则（对应三种互换场景）：
   * - A 移动组带联动 ↔ 占位课无联动：整组去目标位，占位课单门回源位（原有行为）
   * - B 移动组无联动 ↔ 占位课带联动：占位课的联动闭包【整组】回源位；
   *   闭包成员在自己班级的源格若有占位课（displaced），则该课被顶到目标位
   *   （等价于该班级内源↔目标两格互换，不做更深级联）
   * - C 双边都带联动：硬拒绝（多方校验过于复杂，用户取舍）
   * - displaced 自身带联动：硬拒绝（级联拆散，同上取舍）
   *
   * 目标格无本班占位课时为纯移动：returning/displaced 均为空。
   */
  function buildLinkedSwapPlan(
    movingKey: string,
    day: number,
    period: number
  ): LinkedSwapPlan | null {
    const moving = occurrenceIndex.value.byKey.get(movingKey)
    if (!moving) {
      return null
    }

    const moverGroup = resolveLinkedOccurrences(movingKey)
    const moverKeys = new Set(moverGroup.map((member) => member.key))
    const sourceDay = moving.dayOfWeek ?? null
    const sourcePeriod = moving.period ?? null
    const fromStaging = !moving.placed || sourceDay == null || sourcePeriod == null

    // 阶段一：每个组成员在目标格中本班的占位课（非组成员）
    const returning = new Map<string, ScheduleOccurrenceDraft>()
    for (const member of moverGroup) {
      const occupant = occurrencesAt(day, period).find((o) =>
        !moverKeys.has(o.key) &&
        !returning.has(o.key) &&
        o.placed &&
        o.dayOfWeek === day &&
        o.period === period &&
        o.classId === member.classId &&
        (o.subgroupId ?? null) === (member.subgroupId ?? null)
      )
      if (occupant) {
        returning.set(occupant.key, occupant)
      }
    }
    // 无占位 → 纯移动
    if (returning.size === 0) {
      return { moverGroup, returningGroup: [], displacedGroup: [], reject: null }
    }

    // 阶段二：占位课联动闭包一起回源位（B 场景扩展；闭包成员按定义与占位课同槽位）
    const expanded = new Map(returning)
    for (const occupant of [...returning.values()]) {
      for (const partner of resolveLinkedOccurrences(occupant.key)) {
        if (moverKeys.has(partner.key)) continue
        if (
          partner.placed &&
          partner.dayOfWeek === day &&
          partner.period === period
        ) {
          expanded.set(partner.key, partner)
        }
      }
    }

    // 场景 C：移动组带联动 且 占位侧也带联动 → 硬拒绝
    if (moverGroup.length > 1) {
      for (const occupant of expanded.values()) {
        const partner = resolveLinkedOccurrences(occupant.key).find(
          (item) => item.key !== occupant.key && !moverKeys.has(item.key)
        )
        if (partner) {
          return {
            moverGroup,
            returningGroup: [...expanded.values()],
            displacedGroup: [],
            reject: { kind: 'both-linked', occupant, partner }
          }
        }
      }
    }

    // 阶段三：回源成员在自己班级源格上的占位课 → 被顶到目标位（源↔目标两格互换）
    const displaced = new Map<string, ScheduleOccurrenceDraft>()
    if (!fromStaging) {
      for (const member of expanded.values()) {
        const bumped = occurrencesAt(sourceDay, sourcePeriod).find((o) =>
          !moverKeys.has(o.key) &&
          !expanded.has(o.key) &&
          !displaced.has(o.key) &&
          o.placed &&
          o.dayOfWeek === sourceDay &&
          o.period === sourcePeriod &&
          o.classId === member.classId &&
          (o.subgroupId ?? null) === (member.subgroupId ?? null)
        )
        if (bumped) {
          // 被顶替课自身带联动 → 硬拒绝（避免级联拆散其联动组）
          const partner = resolveLinkedOccurrences(bumped.key).find(
            (item) => item.key !== bumped.key && !moverKeys.has(item.key)
          )
          if (partner) {
            return {
              moverGroup,
              returningGroup: [...expanded.values()],
              displacedGroup: [...displaced.values()],
              reject: { kind: 'displaced-linked', occupant: bumped, partner }
            }
          }
          displaced.set(bumped.key, bumped)
        }
      }
    }

    return {
      moverGroup,
      returningGroup: [...expanded.values()],
      displacedGroup: [...displaced.values()],
      reject: null
    }
  }

  /**
   * 对调被联动关系拒绝时的提示文案。
   */
  function linkedSwapRejectMessage(
    reject: NonNullable<LinkedSwapPlan['reject']>
  ): string {
    const { kind, occupant, partner } = reject
    if (kind === 'displaced-linked') {
      return `对调需将【${occupant.courseName}】（${occupant.className}）换到目标时段，但该课程属于合班/同时开课/单双周联动组（关联【${partner.courseName}】（${partner.className}）），校验过于复杂，本次不可调换。请改选其他时段，或先整体调整该联动课程。`
    }
    return `目标格课程【${occupant.courseName}】（${occupant.className}）属于合班/同时开课/单双周联动组（关联【${partner.courseName}】（${partner.className}）），双方均为联动课程时对调校验过于复杂，本次不可调换。请改选其他时段，或先整体调整该联动课程。`
  }

  /**
   * 辅助方法：校验课时是否处于禁止排课时间段
   */
  function checkForbiddenSlot(occ: ScheduleOccurrenceDraft, day: number, period: number, forbiddenRequiredRules: ForbiddenRequiredItem[]): string | null {
    for (const rule of forbiddenRequiredRules) {
      // 1. 课程维度禁止排课规则
      if (rule.targetType === 'course' && rule.courseId === occ.courseId && rule.classId === occ.classId) {
        if (rule.forbiddenSlots && rule.forbiddenSlots.some((s: TimeSlot) => s.dayOfWeek === day && s.period === period)) {
          return `【${occ.courseName}】在此时段禁止排课`
        }
      }
      // 2. 教师维度禁止排课规则（多师同堂：任一共同任教教师命中即违反）
      if (rule.targetType === 'teacher' && rule.targetId != null
        && occurrenceTeacherIds(occ).includes(rule.targetId)) {
        if (rule.forbiddenSlots && rule.forbiddenSlots.some((s: TimeSlot) => s.dayOfWeek === day && s.period === period)) {
          return `教师【${teacherNameOf(occ, rule.targetId)}】在此时段禁止排课`
        }
      }
      // 3. 班级维度禁止排课规则
      if (rule.targetType === 'class' && rule.targetId === occ.classId) {
        if (rule.forbiddenSlots && rule.forbiddenSlots.some((s: TimeSlot) => s.dayOfWeek === day && s.period === period)) {
          return `班级【${occ.className}】在此时段禁止排课`
        }
      }
    }
    return null
  }

  /**
   * 精确的拖拽冲突严重等级校验：Green / Yellow / Red
   *
   * 检测范围：被拖课时的整个联动组（合班/同时开课/单双周）。
   * 判定逻辑沿用单课时的既有规则（教师双占=红、禁排时段=黄、同班同位=互换），
   * 组内成员之间互相豁免；互换时同样校验每个被换回源位的课时。
   */
  function checkDragConflict(
    movingKey: string,
    day: number,
    period: number
  ): { status: 'green' | 'yellow' | 'red'; message: string; hard?: boolean } {
    const moving = occurrenceIndex.value.byKey.get(movingKey)
    if (!moving) {
      return { status: 'green', message: '' }
    }

    const plan = buildLinkedSwapPlan(movingKey, day, period)
    if (!plan) {
      return { status: 'green', message: '' }
    }
    const { moverGroup, returningGroup, displacedGroup, reject } = plan
    const moverKeys = new Set(moverGroup.map((member) => member.key))
    const returningKeys = new Set(returningGroup.map((member) => member.key))
    const displacedKeys = new Set(displacedGroup.map((member) => member.key))
    const sourceDay = moving.dayOfWeek ?? null
    const sourcePeriod = moving.period ?? null
    const fromStaging = !moving.placed || sourceDay == null || sourcePeriod == null
    const ruleSettings = getRuleSettings()

    // ==================== 0. 硬性拦截：双边联动（C）或被顶替课带联动 ====================
    if (reject) {
      return { status: 'red', message: linkedSwapRejectMessage(reject), hard: true }
    }

    // 目标侧 = 移动组 + 被顶替课（都会出现在目标槽位）
    const targetSide = [...moverGroup, ...displacedGroup]

    // ==================== 1. 红色冲突校验：教师分身双占（排除合班） ====================
    let teacherConflictMsg = ''

    // A. 校验目标侧每门课（移动组 + displaced）在目标时段是否有老师冲突
    for (const member of targetSide) {
      const memberTeacherIds = occurrenceTeacherIds(member)
      if (memberTeacherIds.length === 0) continue
      const conflictingTeacherId = memberTeacherIds.find((teacherId) =>
        occurrencesAt(day, period).some((o) =>
          !moverKeys.has(o.key) &&
          !returningKeys.has(o.key) && // 占位课（含联动闭包）会被移出目标时段
          o.key !== member.key &&
          o.placed &&
          o.dayOfWeek === day &&
          o.period === period &&
          occurrenceTeacherIds(o).includes(teacherId) &&
          o.classId !== member.classId &&
          !isCombinedClassPair(member, o, ruleSettings)
        )
      )
      if (conflictingTeacherId != null) {
        teacherConflictMsg = `检测到教师冲突：【${teacherNameOf(member, conflictingTeacherId)}】在同一时段被安排在【${member.className}】和其它班级上课（且未设置合班上课规则）。`
        break
      }
    }

    // B. 校验每个回源位的课时（占位课含联动闭包）在源时段是否有老师冲突
    if (!teacherConflictMsg && !fromStaging && sourceDay != null && sourcePeriod != null) {
      for (const occupant of returningGroup) {
        const occupantTeacherIds = occurrenceTeacherIds(occupant)
        if (occupantTeacherIds.length === 0) continue
        const conflictingTeacherId = occupantTeacherIds.find((teacherId) =>
          occurrencesAt(sourceDay, sourcePeriod).some((o) =>
            !moverKeys.has(o.key) && // 移动组会离开源格子
            !displacedKeys.has(o.key) && // 被顶替课也会离开源格子
            o.key !== occupant.key &&
            o.placed &&
            o.dayOfWeek === sourceDay &&
            o.period === sourcePeriod &&
            occurrenceTeacherIds(o).includes(teacherId) &&
            o.classId !== occupant.classId &&
            !isCombinedClassPair(occupant, o, ruleSettings)
          )
        )
        if (conflictingTeacherId != null) {
          teacherConflictMsg = `检测到对调教师冲突：【${teacherNameOf(occupant, conflictingTeacherId)}】在同一时段被安排在【${occupant.className}】和其它班级上课（且未设置合班上课规则）。`
          break
        }
      }
    }

    if (teacherConflictMsg) {
      return { status: 'red', message: teacherConflictMsg }
    }

    // ==================== 2. 黄色冲突校验：违背禁止排课时间段规则 ====================
    let ruleViolationMsg = ''

    if (ruleSettings && ruleSettings.forbiddenRequired) {
      // 校验目标侧每门课是否落入禁排时间
      for (const member of targetSide) {
        const movingViolation = checkForbiddenSlot(member, day, period, ruleSettings.forbiddenRequired)
        if (movingViolation) {
          ruleViolationMsg = `违反禁止排课限制：${movingViolation}`
          break
        }
      }

      // 校验每个回源时段的课时是否落入禁排时间
      if (!ruleViolationMsg && !fromStaging && sourceDay != null && sourcePeriod != null) {
        for (const occupant of returningGroup) {
          const targetViolation = checkForbiddenSlot(occupant, sourceDay, sourcePeriod, ruleSettings.forbiddenRequired)
          if (targetViolation) {
            ruleViolationMsg = `对调后违反禁止排课限制：${targetViolation}`
            break
          }
        }
      }
    }

    if (ruleViolationMsg) {
      return { status: 'yellow', message: ruleViolationMsg }
    }

    return { status: 'green', message: '' }
  }

  return {
    draftToSlot,
    buildConflictInfo,
    resolveLinkedOccurrences,
    isCombinedClassPair,
    buildLinkedSwapPlan,
    linkedSwapRejectMessage,
    checkDragConflict
  }
}
